import type { CalendarEntryState, Category } from '@klopotun/contracts';
import { formatMoney, minorUnitScale } from '@klopotun/domain';

const STATE_TEXT: Record<CalendarEntryState, string> = {
  paid: 'оплачено',
  planned: 'попереду',
  forecast: 'прогноз',
};

const APPROXIMATELY = '≈';

const NON_BREAKING_SPACE = ' ';

const wholeUnits = new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 0 });

const wholePercent = new Intl.NumberFormat('uk-UA', { style: 'percent', maximumFractionDigits: 0 });

const SMALLEST_SHOWN_SHARE = 0.01;

export const formatCalendarState = (state: CalendarEntryState): string => STATE_TEXT[state];

export const formatBaseMoney = (amountMinor: number, currency: string): string =>
  formatMoney({ amountMinor, currency });

/**
 * The mark every estimate and every converted total carries (design note C5a): a number that was
 * computed rather than paid says so on its face.
 */
export const formatApproximate = (text: string): string =>
  `${APPROXIMATELY}${NON_BREAKING_SPACE}${text}`;

const MINUS_SIGN = '−';

const PLUS_SIGN = '+';

export const formatSignedMoney = (amountMinor: number, currency: string): string => {
  const unsigned = formatBaseMoney(Math.abs(amountMinor), currency);

  if (amountMinor < 0) {
    return `${MINUS_SIGN}${unsigned}`;
  }

  return amountMinor > 0 ? `${PLUS_SIGN}${unsigned}` : unsigned;
};

export const formatMoneyMaybeApproximate = (
  amountMinor: number,
  currency: string,
  isApproximate: boolean,
): string => {
  const exact = formatBaseMoney(amountMinor, currency);

  return isApproximate ? formatApproximate(exact) : exact;
};

/** Whole units without the currency, for a cell too small to say more: "1 299", not "1 299,00 UAH". */
export const formatCompactAmount = (amountMinor: number, currency: string): string =>
  wholeUnits.format(Math.round(amountMinor / minorUnitScale(currency)));

export const formatShare = (share: number): string =>
  share < SMALLEST_SHOWN_SHARE
    ? `<${NON_BREAKING_SPACE}${wholePercent.format(SMALLEST_SHOWN_SHARE)}`
    : wholePercent.format(share);

const capitaliseFirst = (text: string): string =>
  text.length === 0 ? text : `${text[0]?.toLocaleUpperCase('uk-UA') ?? ''}${text.slice(1)}`;

export const formatMonthTitle = (monthName: string): string => capitaliseFirst(monthName);

const NO_CATEGORY_TEXT = 'Без категорії';

export const categoryTitleOf = (category: Category | undefined): string => {
  if (category === undefined) {
    return NO_CATEGORY_TEXT;
  }

  return category.emoji === null ? category.name : `${category.emoji} ${category.name}`;
};
